import Swiper from 'swiper'
import { Navigation, Pagination, Scrollbar } from 'swiper/modules'
import { GWLitElement } from 'lib/lit-element'
import { html, css } from 'vendor/lit'
import { lowPriority } from 'lib/performance'
import { debounce } from 'utils/general'
import styles from 'styles/mixins'

class HHProductThumbnail extends GWLitElement {
  static properties = {
    active: {
      reflect: true,
    },
  }

  constructor() {
    super()
    this.isPaused = false
    this.galleryMobileEl = null
    this.galleryMobileSwiper = null
    this.desktopMQ = window.matchMedia('(min-width: 750px)')
    this.modalParent = this.closest('stl-dialog');
    this.transitionEventParent = this.closest('.stl-modal') ?? document
    console.log('modalParent', this.modalParent)

    this.debouncedDispatchChangeEvent = debounce(this.dispatchChangeEvent.bind(this), 200)
    this.onDialogOpen = this.initSlider.bind(this)
    this.onPdpTransitionComplete = this.onPdpTransitionComplete.bind(this)
  }

  initSlider() {
    this.galleryMobileEl = this.querySelector('.gallery-mobile')
    if (!this.galleryMobileEl) return false
    this.galleryMobileSwiper?.destroy(true, true)
    this.galleryMobileSwiper = null

    if (this.getAttribute('data-media-status') === 'loading') {
      if (this.resolvePendingMediaStatus()) {
        return this.initSlider()
      }

      console.debug('PDP: Media Slider - Waiting for media to load')
      document.addEventListener('pdp:media-loaded', (e) => {
        console.debug('PDP: Media Slider - Media loaded', e.detail)
        setTimeout(() => { this.initSlider() }, 100)
      }, { once: true })

      return
    }

    const paginationEl = this.galleryMobileEl.querySelector('.swiper-pagination')

    console.log('gallery width', this.galleryMobileEl.offsetWidth)
    console.log('viewport width', window.innerWidth)
    console.log('slide width including gaps', (window.innerWidth - 20) / 1.25)
    this.galleryMobileSwiper = new Swiper(this.galleryMobileEl, {
      modules: [Navigation, Pagination, Scrollbar],
      spaceBetween: 10,
      slidesPerView: 1.3,
      centeredSlides: !this.modalParent,
      loop: true,
      navigation: false,
      pagination: this.modalParent || !paginationEl ? false : {
        el: paginationEl,
        clickable: true,
      },
      scrollbar: this.modalParent && paginationEl ? {
        el: paginationEl,
        draggable: true,
      } : false,
      breakpoints: {
        750: {
          slidesPerView: 1,
        },
      },
    })

    this.galleryMobileSwiper.on('slideChange', this.debouncedDispatchChangeEvent)
  }

  get regionalMediaCountries() {
    try {
      return JSON.parse(this.dataset.regionalMediaCountries || '[]')
    } catch (error) {
      console.error('PDP: Error parsing regional media countries', error)
      return []
    }
  }

  markMediaLoaded(isInternational = false, detail = {}) {
    this.setAttribute('data-media-status', 'loaded')
    this.setAttribute('international-media', isInternational ? 'true' : 'false')
    this.classList.remove('is-loading')
    this.closest('.product__media-gallery')?.classList.remove('is-loading')
    document.dispatchEvent(new CustomEvent('pdp:media-loaded', { detail }))
  }

  resolvePendingMediaStatus() {
    const regionalMediaEls = [...this.querySelectorAll('[data-regional-media]')]

    if (this.dataset.autoEnableRegionalMedia === 'true') {
      regionalMediaEls.forEach(el => el.removeAttribute('aria-hidden'))
      this.markMediaLoaded(true, { success: true, source: 'gallery' })
      return true
    }

    if (!regionalMediaEls.length) {
      this.markMediaLoaded(false, { success: true, source: 'gallery' })
      return true
    }

    if (!window.countryData) return false

    const { handle, name } = window.countryData
    const regionalMediaCountries = this.regionalMediaCountries
    const isInternational = regionalMediaCountries.includes(name) || regionalMediaCountries.includes(handle)

    if (isInternational) {
      regionalMediaEls.forEach(el => el.removeAttribute('aria-hidden'))
    } else {
      regionalMediaEls.forEach(el => el.remove())
    }

    this.markMediaLoaded(isInternational, {
      success: isInternational,
      data: window.countryData,
      source: 'gallery',
    })

    return true
  }

  dispatchChangeEvent() {
    document.dispatchEvent(new CustomEvent('gallery:change'))
  }

  onPdpTransitionComplete() {
    this.initSlider()
    this.loadImagesOnIdle()
  }

  connectedCallback() {
    this.querySelectorAll('.thumb-item').forEach(element => {
      element.addEventListener('mouseenter', e => this.changeMedia(e))
    })

    this.initSlider()
    this.loadImagesOnIdle()

    this.modalParent?.addEventListener('dialog:open', this.onDialogOpen)
    this.modalParent?.addEventListener('shop-the-look:modal-update', this.onDialogOpen)
    this.transitionEventParent.addEventListener('pdp-transition:complete', this.onPdpTransitionComplete)

    super.connectedCallback()
  }

  disconnectedCallback() {
    this.modalParent?.removeEventListener('dialog:open', this.onDialogOpen)
    this.modalParent?.removeEventListener('shop-the-look:modal-update', this.onDialogOpen)
    this.transitionEventParent.removeEventListener('pdp-transition:complete', this.onPdpTransitionComplete)
    this.galleryMobileSwiper?.destroy(true, true)
    this.galleryMobileSwiper = null
    
    super.disconnectedCallback()
  }

  changeMedia(e) {
    const activeMediaId = e.target.getAttribute('media-id')
    this.querySelectorAll('.main-item').forEach(element => {
      if (element.getAttribute('media-id') === activeMediaId) {
        element.classList.add('is-active')
      } else {
        element.classList.remove('is-active')
      }
    })

    this.querySelectorAll('.thumb-item').forEach(element => {
      if (element.getAttribute('media-id') === activeMediaId) {
        element.classList.add('is-active')
      } else {
        element.classList.remove('is-active')
      }
    })

    this.debouncedDispatchChangeEvent()
  }

  loadImages() {
    const images = [
      ...(this.desktopMQ.matches && !this.modalParent
        ? this.querySelectorAll('.main-item img')
        : this.galleryMobileEl.querySelectorAll('img')),
    ]
    for (const image of images) {
      image.setAttribute('loading', 'eager')
    }
  }

  loadImagesOnIdle() {
    lowPriority(this.loadImages.bind(this), 2)
  }

  // render() {
  //   return html`
  //     <slot name="gallery-mobile"></slot>
  //     <slot name="gallery-thumbnails"></slot>
  //     <div class="product-thumbnails__main">
  //       <slot name="sale-badge"></slot>
  //       <slot name="main-item"></slot>
  //     </div>
  //   `
  // }

  render() {
    return html`
      <slot name="gallery-mobile"></slot>
      ${this.modalParent ? html`` : html`
        <div class="product-thumbnails__thumb">
          <slot name="thumb-item"></slot>
        </div>
        <div class="product-thumbnails__main" style="width: 100%; position: relative;">
          <slot name="sale-badge"></slot>
          <slot name="main-item"></slot>
        </div>
      `}
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host {
        display: flex;
      }

      .product-thumbnails__thumb {
        width: 75px;
      }

      .product-thumbnails__main {
        width: 100%;
        position: relative;
      }

      @media screen and (max-width: 749.9px) {
        :host {
          aspect-ratio: var(--hh-aspect-ratio);
          max-height: var(--hh-max-height);
          flex-direction: column-reverse;
          gap: 10px;
        }

        .product-thumbnails__main,
        .product-thumbnails__thumb {
          display: none;
        }
      }
    `,
  ]
}

customElements.define('hh-product-thumbnails', HHProductThumbnail)
